import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, forwardRef,
  ContentChild, AfterContentInit, AfterViewInit, Renderer } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ControlValueAccessor, FormControlDirective,
  FormControlName, NgModel } from '@angular/forms';

declare var jQuery: any;
declare var moment: any;
let nextId = 0;

export class PuiDateRange {
  start: Date;
  end: Date;
};

export class PuiDateRangeChange {
  source: PuiDateRangeComponent;
  dateRange: PuiDateRange;
};

export const PUI_DATE_RANGE_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => PuiDateRangeComponent),
  multi: true
};

@Component({
  exportAs: 'puiInput',
  selector: 'pui-daterange',
  providers: [PUI_DATE_RANGE_CONTROL_VALUE_ACCESSOR],
  templateUrl: 'daterange.component.html'
})
export class PuiDateRangeComponent implements AfterContentInit, AfterViewInit, ControlValueAccessor {
  @Input() id = `pui-daterange-${nextId++}`;
  @Input() name: string;
  @Input() label: string;
  @Input() placeholder = '';
  @Input() disabled: boolean;
  @Input() format = 'YYYY/MM/DD';
  @Input() enableTimePicker: boolean;
  @Input() timePickerIncrement = 30;
  @Input() ranges: Array<any>;
  @Input() useAsFilter = true;

  @Output() clear = new EventEmitter();
  @Output() change = new EventEmitter<PuiDateRangeChange>();

  @ViewChild('input') input: ElementRef;
  @ContentChild(NgModel) ngModel: NgModel;
  @ContentChild(FormControlName) controlName: FormControlDirective;
  @ContentChild(FormControlDirective) controlDirective: FormControlDirective;

  _control: FormControl = new FormControl();
  private displayValue = '';
  private startDate: Date;
  private endDate: Date;

  constructor(private renderer: Renderer) { }

  private onTouchedCallback: () => void = () => { };
  private onChangeCallback: (_: any) => void = () => { };

  get value() {
    return this.displayValue;
  }

  set value(val: any) {
    this.displayValue = val;
  }

  writeValue(v: any): void {
    if (v && v.start && v.end) {
      this.displayValue = this.formatDisplayValue(v.start, v.end);
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  _onBlur() {
    this.onTouchedCallback();
  }

  ngAfterContentInit() {
    if (this.controlDirective) {
      this._control = this.controlDirective.control;
    } else if (this.controlName) {
      this._control = this.controlName.control;
    }
    if (this._control.disabled) {
      this.disabled = true;
    }
  }

  ngAfterViewInit() {
    if (this.ngModel) {
      let m = this.ngModel.model;
      if (m && m.start && m.end) {
        this.startDate = m.start;
        this.endDate = m.end;
      }
    }

    let el = jQuery(this.input.nativeElement);
    el.daterangepicker({
      autoUpdateInput: false,
      startDate: this.startDate,
      endDate: this.endDate,
      ranges: this.ranges,
      timePicker: this.enableTimePicker,
      timePickerIncrement: this.timePickerIncrement,
      locale: {
        cancelLabel: this.useAsFilter ? 'Clear' : undefined,
        format: this.format
      }
    }, (start: any, end: any, label: any) => {
      this.value = this.formatDisplayValue(start, end);
      el.val(this.value);
      this.startDate = start.toDate();
      this.endDate = end.toDate();
      this._emitChangeEvent(true);
      });
    el.on('cancel.daterangepicker', (e: any, picker: any) => {
      picker.setStartDate(new Date());
      picker.setEndDate(new Date());
      el.val(this.placeholder);
      this._emitClearEvent();
    });
    if (this.startDate && this.endDate) {
      this._emitChangeEvent();
    }
  }

  formatDisplayValue(start: any, end: any): string {
    return (moment.isMoment(start) ? start : moment(start)).format(this.format) + ' - ' +
      (moment.isMoment(end) ? end : moment(end)).format(this.format);
  }

  _onChange(e: any) {
    e.stopPropagation();
    if (!e.target.value && (this.startDate || this.endDate)) {
      this._emitClearEvent();
    }
  }

  _emitClearEvent() {
    this.startDate = null;
    this.endDate = null;
    this.clear.emit();
    this.onTouchedCallback();
    this.onChangeCallback(null);
  }

  _emitChangeEvent(changed = false) {
    let range = <PuiDateRange>{
      start: this.startDate,
      end: this.endDate
    };
    this.change.emit(<PuiDateRangeChange>{
      source: this,
      dateRange: range
    });
    if (changed) {
      this.onTouchedCallback();
      this.onChangeCallback(range);
    }
  }

  _iconClick(e: Event) {
    let el = this.input.nativeElement;
    this.renderer.invokeElementMethod(el, 'focus');
  }
}
